/** @jsxImportSource @emotion/react */
import * as React from "react";
import clsx from "clsx";
import Dropzone from "react-dropzone";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { css } from "@emotion/react";
import { ExternalLinkWrapper } from "../../../components/ExternalLinkWrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

interface Props {
  name: string;
  label: string;
  helpText?: string;
  onDrop: (acceptedFiles: File[]) => void;
  onSelectedFileRemove?: (file: File) => void;
  selectedFiles?: File[];
  accept?: string;
  maxFiles?: number;
  children: React.ReactNode;
}

const readFile = async (file: File): Promise<string | undefined> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.addEventListener(
      "load",
      function () {
        resolve(reader.result?.toString());
      },
      false
    );
  });
};

const labelWrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const linkStyle = css`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  color: ${colors.textSecondary};
  &:hover {
    color: ${colors.textPrimary};
  }
  margin-bottom: 8px;
  text-decoration: none !important;
`;

export const IMAGE_GUIDE_LINK =
  "https://heapsapp.notion.site/Image-guide-a735f884aa18462dbd1945d9b9122681";

export const DropzoneFormField: React.FC<Props> = (props) => {
  const files = props?.selectedFiles;
  const onSelectedFileRemove = props?.onSelectedFileRemove;
  const [filesWithData, setFilesWithData] = React.useState<
    Array<[File, string | undefined]>
  >([]);

  React.useEffect(() => {
    (async () => {
      let result: Array<[File, string | undefined]> = [];

      if (!Array.isArray(files)) {
        return setFilesWithData(result);
      }

      for (const file of files) {
        result.push([file, await readFile(file)]);
      }

      return setFilesWithData(result);
    })();
  }, [files]);

  return (
    <div className={clsx("form-group w-100")}>
      <div css={labelWrapperStyle}>
        {props.label && (
          <StyledText
            color={colors.textPrimary}
            className="bodySubtextSemibold mb-2"
          >
            {props.label}
          </StyledText>
        )}
        <ExternalLinkWrapper href={IMAGE_GUIDE_LINK} css={linkStyle}>
          <StyledText className="bodySubtextRegular mb-0">Guide</StyledText>
          <StyledText className="bodySubtextRegular mb-0">
            <FontAwesomeIcon icon={solid("arrow-up-right-from-square")} />
          </StyledText>
        </ExternalLinkWrapper>
      </div>
      {props.children}
      <Dropzone
        onDrop={props.onDrop}
        accept={props.accept}
        maxFiles={props.maxFiles}
      >
        {({ getRootProps, getInputProps }) => (
          <section className="card mb-0">
            <div {...getRootProps()} className="card-body">
              <input {...getInputProps()} />
              <div className="card-alert">
                <StyledText
                  color={colors.textPrimary}
                  className="bodyTextRegular mt-0 mb-0"
                >
                  Drag 'n' drop image(s) here, or <b>click to select files</b>
                </StyledText>
                {props.helpText && (
                  <StyledText
                    color={colors.textSecondary}
                    className="bodySubtextRegular mt-2 mb-0"
                  >
                    {props.helpText}
                  </StyledText>
                )}
              </div>
            </div>
          </section>
        )}
      </Dropzone>

      <div className="row mt-2">
        {filesWithData.map(([file, dataUrl], i) => (
          <React.Fragment key={file.name}>
            <div className="col-md-3">
              <div className="card mb-0">
                <img src={dataUrl} alt={file.name} />
                {onSelectedFileRemove && (
                  <div className="card-footer p-2">
                    <div className="caption text-right">
                      <button
                        type="button"
                        className={clsx("btn btn-warning btn-sm")}
                        onClick={() => onSelectedFileRemove(file)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {(i + 1) % 4 === 0 && <div className="w-100" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
